import { useContext, useEffect, useState } from "react";
import { Box, Checkbox, Divider, FormControlLabel, IconButton, Tooltip, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Grid } from "@mui/material";
import { Paper } from "@mui/material";
import { WorkHoursSummary } from "./WorkHoursSummary";
import { ClientHoursSummary } from "./ClientHoursSummary";
import { TimeOffSummary } from "./TimeOffSummary";
import { Payment } from "../types";
import { Avatar } from "../../../components/fieldsViews/fields/Avatar";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { useField } from "../../../api/useField";
import { FileField } from "../../../components/fieldsViews/fields/FileField";
import { SalariesUploadButton } from "./SalariesUploadButton";
import { formatCurrency } from "../../../utils/formatCurrency";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import { NoteField } from "./NoteField";
import { PaymentCorrectionsDisplay } from "./PaymentCorrectionsDisplay";
import { SPECIAL_USERS } from "../../../utils/constants";
import { calculateServiceProviderPay, STAFF_REMUNERATION_FIELDS } from "../utils/serviceProviderPay";
import {
    getMonthContractPeriods,
    getMonthContractTypes,
    isMixedContractMonth,
    isServiceProviderMonth,
} from "../utils/monthContractType";
import { renderFieldView } from "../../../components/fieldsViews/renderFieldView";
import { BonusChip } from "./BonusChip";
import { useRecords } from "../../../api/useRecords";
import { StaffAgreementFiles } from "./StaffAgreementFiles";
import { ServiceProviderPaySummary } from "./ServiceProviderPaySummary";
import { useAgreementTypeLabels } from "../utils/useAgreementTypeLabels";
import { OwnPayslipFiles } from "./OwnPayslipFiles";

export const EmployeeSummary = () => {
    const { currentUser, isCurrentUserAdmin, payment, selectedMonth, selectedYear, userId, staffAgreement, workLogs, payData } =
        useContext(StaffWorkLogsContext);
    const { hasFinanceAccess, moneyAvailability, ownRemunerations, agreementsWithoutPayments, monthCorrections } = payData;

    const { formatedField: documentField } = useField("staff_monthly_payments", "files");
    const { formatedField: professionalCategoryField } = useField("staff_agreements", "professional_category");
    const agreementTypeLabels = useAgreementTypeLabels();
    const { data: financeRemunerations, status: financeRemunerationsStatus } = useRecords("staff_remuneration", STAFF_REMUNERATION_FIELDS, {
        extraOptions: {
            filter: {
                operations_staff_agreement_id: {
                    _eq: staffAgreement?.id,
                },
            },
            pageSize: -1,
        },
        enabled: hasFinanceAccess && Boolean(staffAgreement?.id),
    });

    const updatePayment = useUpdateRecord();
    const [adminValidated, setAdminValidated] = useState(false);

    useEffect(() => {
        setAdminValidated(payment?.admin_validated || false);
    }, [payment]);

    const isAdminOrCurrentUser = isCurrentUserAdmin || currentUser?.id === userId;

    const handlePaymentUpdate = async (data: Partial<Payment>) => {
        if (payment) {
            updatePayment.mutateAsync({
                entity: "staff_monthly_payments",
                id: payment.id,
                data,
            });
        }
    };

    const isServiceProvider = staffAgreement?.agreement_type === "services_provider";
    const pageAgreementId = String(staffAgreement?.id);
    const remunerations = hasFinanceAccess
        ? financeRemunerations?.items ?? []
        : (ownRemunerations ?? []).filter((remuneration) => remuneration.operations_staff_agreement_id === pageAgreementId);
    const areRemunerationsReady = hasFinanceAccess ? financeRemunerationsStatus === "success" : ownRemunerations !== null;
    const isAgreementWithoutPayments = !hasFinanceAccess && agreementsWithoutPayments.includes(pageAgreementId);

    const weeklyHoursSchedule = staffAgreement?.staff_weekly_hours ?? [];
    const selectedMonthDate = new Date(selectedYear, selectedMonth);
    const monthContractTypes = getMonthContractTypes({
        weeklyHoursSchedule,
        agreementType: staffAgreement?.agreement_type,
        monthDate: selectedMonthDate,
    });
    const isProviderMonth = isServiceProviderMonth(monthContractTypes);
    const serviceProviderPay = calculateServiceProviderPay({
        workLogs,
        remunerations,
        monthDate: selectedMonthDate,
        weeklyHoursSchedule,
    });
    const contractPeriods = getMonthContractPeriods(weeklyHoursSchedule, selectedMonthDate);
    const isMoneyReady = moneyAvailability.state === "ready";
    const canShowServiceProviderPay =
        isAdminOrCurrentUser && isProviderMonth && isMoneyReady && areRemunerationsReady && !isAgreementWithoutPayments;
    const canValidatePayment =
        hasFinanceAccess && isMoneyReady && (currentUser?.id === SPECIAL_USERS.joana_director || isCurrentUserAdmin);

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2, padding: 4 }}>
                {staffAgreement ? (
                    <>
                        <Box display="flex" alignItems="top" justifyContent="space-between" gap={2}>
                            <Box display="flex" flexDirection="column" alignItems="start">
                                <Box display="flex" alignItems="center" gap={2} alignSelf="start">
                                    <Avatar value={staffAgreement.user.avatar as string} />
                                    <Typography variant="h6" color="primary">
                                        {staffAgreement.user.first_name} {staffAgreement.user.last_name}
                                    </Typography>
                                    {isCurrentUserAdmin && staffAgreement.id && (
                                        <Tooltip title="Abrir acordo de trabalho" arrow>
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                component="a"
                                                href={`/staff_agreements/${staffAgreement.id}/detail`}
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                <OpenInNew fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box>
                                    <Box display="flex" alignItems="center" gap={2}>
                                        {canShowServiceProviderPay && (
                                            <ServiceProviderPaySummary
                                                pay={serviceProviderPay}
                                                isMixedMonth={isMixedContractMonth(monthContractTypes)}
                                                contractPeriods={contractPeriods}
                                                agreementTypeLabels={agreementTypeLabels}
                                            />
                                        )}
                                        {isAdminOrCurrentUser && isProviderMonth && isAgreementWithoutPayments && (
                                            <Typography variant="body2" color="text.secondary">
                                                Valor para recibo indisponível até haver registo de pagamento
                                            </Typography>
                                        )}
                                        {hasFinanceAccess && isAdminOrCurrentUser && isProviderMonth && payment && (
                                            <SalariesUploadButton
                                                selectedYear={selectedYear}
                                                selectedMonth={selectedMonth}
                                                type="service_provider_receipt"
                                                buttonText="Enviar recibo"
                                                display="icon"
                                                currentUser={currentUser}
                                            />
                                        )}
                                    </Box>
                                    {moneyAvailability.state === "unavailable" && (
                                        <Typography variant="body2" color="error">
                                            Valores indisponíveis: {moneyAvailability.reason}
                                        </Typography>
                                    )}
                                    {payment?.amount_receipt_to_transfer && (
                                        <Typography>
                                            Total a receber: {formatCurrency(payment.amount_receipt_to_transfer)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    {payment?.subsidy && (
                                        <Typography variant="body1">
                                            Subsídio alimentação: {formatCurrency(payment.subsidy)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box display="flex" alignItems="center">
                                    <Typography variant="body1" width="180px">
                                        Categoria Profissional:
                                    </Typography>
                                    {professionalCategoryField &&
                                        renderFieldView(
                                            professionalCategoryField,
                                            { professional_category: staffAgreement?.professional_category },
                                            "group"
                                        )}
                                </Box>

                                {isCurrentUserAdmin && (
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body1" width="180px">
                                            Pagamento de Bónus:
                                        </Typography>
                                        <BonusChip staffAgreement={staffAgreement} />
                                    </Box>
                                )}
                            </Box>
                            <Box display="flex" alignItems="start" flexDirection="column">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={adminValidated}
                                            disabled={!canValidatePayment}
                                            onChange={(e) => {
                                                setAdminValidated(e.target.checked);
                                                handlePaymentUpdate({ admin_validated: e.target.checked });
                                            }}
                                        />
                                    }
                                    label="Validado por admin"
                                />
                                    <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                                    <Typography variant="body1">Início:</Typography>
                                    <Typography variant="body1"> {staffAgreement.work_start_date}</Typography>
                                </Box>
                            </Box>
                            {documentField && payment?.id && (
                                <FileField
                                    value={payment.files}
                                    field={documentField}
                                    fieldView="group"
                                    entity="staff_monthly_payments"
                                    id={payment.id}
                                    extraOptions={{
                                        fileFolder: "a80a2485-67b2-4c60-98bb-4b5a61d8bd39",
                                        field: "files",
                                        instanceToUse: "finance",
                                        hidden: {
                                            detail: staffAgreement?.agreement_type === "services_provider",
                                        },
                                    }}
                                />
                            )}
                            {!hasFinanceAccess && payment?.payslips && <OwnPayslipFiles payslips={payment.payslips} />}
                            {staffAgreement?.id && (
                                <StaffAgreementFiles
                                    staffAgreementId={staffAgreement.id}
                                    selectedMonth={selectedMonth}
                                    selectedYear={selectedYear}
                                    files={staffAgreement.files || []}
                                />
                            )}

                        </Box>
                        <Divider sx={{ my: 4 }} />
                        <Grid container spacing={2} display="flex" alignItems="top" justifyContent="space-between">
                            <Grid item>
                                <WorkHoursSummary
                                    isAdminOrCurrentUser={isAdminOrCurrentUser}
                                    isServiceProvider={isServiceProvider}
                                />
                            </Grid>
                            <Grid item>
                                <ClientHoursSummary />
                            </Grid>
                            <Grid item>
                                <TimeOffSummary />
                            </Grid>
                            <Grid item>
                                <PaymentCorrectionsDisplay
                                    payment={monthCorrections}
                                    canEditCorrections={hasFinanceAccess && isCurrentUserAdmin}
                                    onUpdate={handlePaymentUpdate}
                                />
                            </Grid>
                            {hasFinanceAccess && (
                                <Grid item>
                                    <NoteField adminValidated={adminValidated} handlePaymentUpdate={handlePaymentUpdate} />
                                </Grid>
                            )}
                        </Grid>
                    </>
                ) : (
                    <Typography color="error">Sem acordo de colaborador</Typography>
                )}
            </Paper>
        </Box>
    );
};
